import { useEffect, useState, useRef, useMemo, useCallback, memo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Lock, Sparkles, Mail, Loader2, ArrowLeft, Heart, MessageCircle, Send, X, Eye, EyeOff, KeyRound, Grid3x3, Film, Bookmark, Image as ImageIcon, Play, ChevronDown, MoreHorizontal, Smile, Phone, Video as VideoIcon, Home, Search, User, ChevronLeft, ChevronRight, Share2, UserCheck, Star, Flame, Crown, Gem, Camera, Music, MapPin, Coffee, Plane, Zap, Moon, Sun, Cloud, Gift, Flower2 } from "lucide-react";
import { toast } from "sonner";

const API = "";

// Reusable password input with eye toggle
const PasswordInput = ({ value, onChange, placeholder, testId, onKeyDown }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 pr-11 text-white placeholder-white/30 outline-none focus:border-pink-400/50"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        tabIndex={-1}
        aria-label={show ? "Ocultar contraseña" : "Ver contraseña"}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 p-1"
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
};

const useTokenForCreator = (username) => {
  const key = `jgfans_${username}`;
  const [token, setToken] = useState(() => localStorage.getItem(key) || "");
  const save = (t) => {
    if (t) localStorage.setItem(key, t);
    else localStorage.removeItem(key);
    setToken(t);
  };
  return [token, save];
};

// ============== GATE PAGE: /jgfans/:username ==============
export const JGFansGate = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [preview, setPreview] = useState([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showLogin, setShowLogin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useTokenForCreator(username);

  useEffect(() => {
    if (token) {
      navigate(`/jgfans/${username}/feed`);
      return;
    }
    Promise.all([
      fetch(`${API}/api/jgfans/${username}/info`).then((r) => (r.ok ? r.json() : null)),
      fetch(`${API}/api/jgfans/${username}/feed/preview`).then((r) => (r.ok ? r.json() : { items: [] })),
    ])
      .then(([i, p]) => {
        setInfo(i);
        setPreview(p.items || []);
      })
      .finally(() => setLoading(false));
  }, [username, token, navigate]);

  const startCheckout = async () => {
    if (!email || !/.+@.+\..+/.test(email)) {
      toast.error("Ingresa un email válido");
      return;
    }
    if (!password || password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    try {
      const r = await fetch(`${API}/api/jgfans/${username}/bold/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, originUrl: window.location.origin }),
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Error al crear el link de pago");
        return;
      }
      window.location.href = d.url;
    } catch (e) {
      console.error(e);
      toast.error("Error de red");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white/50 animate-spin" />
      </div>
    );
  }
  if (!info) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center text-center px-6">
        <Lock className="w-12 h-12 text-white/30 mb-4" />
        <p className="text-white/70">JGFans no disponible para esta modelo</p>
        <button
          onClick={() => navigate(`/${username}`)}
          className="mt-4 text-pink-400 text-sm underline"
        >
          Volver al perfil
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-[#1a0d1a] to-black text-white">
      <div className="max-w-md mx-auto px-5 pt-8 pb-12">
        <button
          onClick={() => navigate(`/${username}`)}
          className="text-white/60 hover:text-white text-sm flex items-center gap-1 mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Volver al perfil
        </button>

        {/* Hero */}
        <div className="text-center mb-8">
          <div className="relative inline-block mb-4">
            <div className="absolute -inset-1 bg-gradient-to-r from-pink-500 via-purple-500 to-pink-500 rounded-full blur opacity-60 animate-pulse" />
            <img
              src={info.avatar || "/logo.png"}
              alt=""
              className="relative w-24 h-24 rounded-full object-cover border-2 border-white/20"
            />
          </div>
          <h1 className="text-3xl font-black tracking-tight">{info.name}</h1>
          <p className="text-white/40 text-sm font-mono">@{info.username}</p>
          <div className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-500/30 rounded-full text-xs">
            <Sparkles className="w-3 h-3 text-pink-400" />
            <span className="font-bold tracking-wide">JGFans · ACCESO VIP</span>
          </div>
        </div>

        {/* Blurred preview grid */}
        <div className="relative mb-8 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-3 gap-1">
            {(preview.length > 0 ? preview : Array(6).fill(null)).slice(0, 6).map((it, i) => (
              <div
                key={it?.id || i}
                className="aspect-square bg-gradient-to-br from-pink-900/40 to-purple-900/40 relative overflow-hidden"
              >
                {it?.mediaUrl && (
                  <img
                    src={it.mediaUrl}
                    alt=""
                    className="w-full h-full object-cover"
                    style={{ filter: "blur(18px) brightness(0.6)" }}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black via-black/40 to-transparent">
            <div className="bg-black/70 backdrop-blur-md border border-white/10 rounded-2xl px-6 py-4 text-center">
              <Lock className="w-7 h-7 mx-auto mb-2 text-pink-400" />
              <p className="text-sm text-white/80">
                <strong>{info.mediaCount}</strong> contenidos exclusivos
              </p>
              <p className="text-xs text-white/40 mt-0.5">desbloquea para ver todo</p>
            </div>
          </div>
        </div>

        {/* Description */}
        <p className="text-center text-white/70 text-sm mb-8 leading-relaxed px-2">
          {info.description}
        </p>

        {/* Pricing card */}
        <div className="bg-gradient-to-br from-pink-500/10 to-purple-500/10 border border-pink-500/30 rounded-3xl p-6 mb-6">
          <div className="flex items-baseline justify-center gap-1 mb-1">
            {info.displayCurrency === "USD" ? (
              <>
                <span className="text-5xl font-black">${Number(info.displayPrice || 0).toFixed(2)}</span>
                <span className="text-white/50 text-sm ml-1">USD / mes</span>
              </>
            ) : (
              <>
                <span className="text-5xl font-black">${(info.priceCOP || 35000).toLocaleString("es-CO")}</span>
                <span className="text-white/50 text-sm ml-1">COP / mes</span>
              </>
            )}
          </div>
          <p className="text-center text-xs text-white/50 mb-5">
            Acceso completo por {info.accessDays} días · Pago seguro vía Bold
            {info.displayCurrency === "USD" && (
              <>
                <br />
                <span className="text-white/40">
                  Se cobra ≈ ${(info.priceCOP || 35000).toLocaleString("es-CO")} COP al tipo de cambio actual
                </span>
              </>
            )}
          </p>
          <ul className="space-y-2 mb-6 text-sm">
            <li className="flex items-center gap-2 text-white/80">
              <Heart className="w-4 h-4 text-pink-400 flex-shrink-0" /> Feed completo de contenido VIP
            </li>
            <li className="flex items-center gap-2 text-white/80">
              <MessageCircle className="w-4 h-4 text-pink-400 flex-shrink-0" /> Chat directo conmigo
            </li>
            <li className="flex items-center gap-2 text-white/80">
              <Sparkles className="w-4 h-4 text-pink-400 flex-shrink-0" /> Contenido nuevo cada semana
            </li>
          </ul>

          {!showLogin ? (
            <>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                data-testid="jgfans-email-input"
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 outline-none focus:border-pink-400/50 mb-2"
              />
              <div className="mb-3">
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Crea una contraseña (6+ caracteres)"
                  testId="jgfans-password-input"
                />
              </div>
              <button
                onClick={startCheckout}
                data-testid="jgfans-subscribe-btn"
                className="w-full py-3.5 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 rounded-xl font-bold text-base transition-all shadow-lg shadow-pink-500/30"
              >
                {info.displayCurrency === "USD"
                  ? `Pagar $${Number(info.displayPrice || 0).toFixed(2)} USD 🔥`
                  : `Pagar $${(info.priceCOP || 35000).toLocaleString("es-CO")} COP 🔥`}
              </button>
              <button
                onClick={() => setShowLogin(true)}
                className="w-full mt-3 text-white/50 hover:text-white text-xs"
              >
                Ya estoy suscrito · entrar
              </button>
            </>
          ) : (
            <LoginPanel
              username={username}
              onClose={() => setShowLogin(false)}
              onSuccess={(t) => {
                setToken(t);
                navigate(`/jgfans/${username}/feed`);
              }}
            />
          )}
        </div>

        <p className="text-[11px] text-white/30 text-center leading-relaxed">
          Pago seguro vía Stripe · Renovación manual al vencer · Tus datos están protegidos
        </p>

        {/* Ambassador CTA */}
        <a
          href="/embajadoras"
          data-testid="ambassador-cta-jgfans"
          className="mt-6 group flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-rose-500/10 to-amber-500/10 border border-rose-400/30 hover:from-rose-500/20 hover:to-amber-500/20 hover:border-rose-300/50 transition-all"
        >
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-amber-300 font-bold mb-0.5">
              ¿Eres creadora?
            </p>
            <p className="text-xs text-white/80 leading-snug">
              Únete a nuestro equipo de embajadoras
            </p>
          </div>
          <span className="text-rose-300 text-lg group-hover:translate-x-1 transition-transform flex-shrink-0">
            →
          </span>
        </a>
      </div>
    </div>
  );
};

// ============== LOGIN PANEL (email + password) ==============
const LoginPanel = ({ username, onClose, onSuccess }) => {
  const [mode, setMode] = useState("login"); // login | forgot | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submitLogin = async () => {
    if (!email || !password) {
      toast.error("Completa email y contraseña");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/jgfans/${username}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Email o contraseña incorrectos");
        return;
      }
      onSuccess(d.token);
    } finally {
      setBusy(false);
    }
  };

  const requestReset = async () => {
    if (!email || !/.+@.+\..+/.test(email)) {
      toast.error("Ingresa un email válido");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/jgfans/${username}/password/forgot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Error");
        return;
      }
      toast.success("Te enviamos un código a tu email");
      setMode("reset");
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async () => {
    if (resetCode.length < 4 || newPassword.length < 6) {
      toast.error("Código + contraseña nueva (6+ caracteres)");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/jgfans/${username}/password/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code: resetCode, newPassword }),
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Error");
        return;
      }
      toast.success("Contraseña actualizada. Entrando…");
      onSuccess(d.token);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-white/60">
          {mode === "login" && "Entrar con email + contraseña"}
          {mode === "forgot" && "Recuperar contraseña"}
          {mode === "reset" && "Ingresa el código que recibiste"}
        </p>
        <button onClick={onClose} className="text-white/40 hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>

      {mode === "login" && (
        <>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@email.com"
            data-testid="jgfans-login-email"
            className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 outline-none focus:border-pink-400/50"
          />
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Tu contraseña"
            testId="jgfans-login-password"
            onKeyDown={(e) => e.key === "Enter" && submitLogin()}
          />
          <button
            onClick={submitLogin}
            disabled={busy}
            data-testid="jgfans-login-submit"
            className="w-full py-3 bg-gradient-to-r from-pink-500 to-purple-500 rounded-xl font-bold text-sm disabled:opacity-50"
          >
            {busy ? "Verificando…" : "Entrar"}
          </button>
          <button
            onClick={() => setMode("forgot")}
            className="w-full text-xs text-pink-300 hover:text-pink-200 inline-flex items-center justify-center gap-1"
            data-testid="jgfans-forgot-password"
          >
            <KeyRound className="w-3 h-3" />
            Olvidé mi contraseña
          </button>
        </>
      )}

      {mode === "forgot" && (
        <>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@email.com"
            className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 outline-none focus:border-pink-400/50"
          />
          <button
            onClick={requestReset}
            disabled={busy}
            className="w-full py-3 bg-gradient-to-r from-pink-500 to-purple-500 rounded-xl font-bold text-sm disabled:opacity-50"
          >
            {busy ? "Enviando…" : "Enviar código por email"}
          </button>
          <button
            onClick={() => setMode("login")}
            className="w-full text-xs text-white/50 hover:text-white"
          >
            Volver al login
          </button>
        </>
      )}

      {mode === "reset" && (
        <>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={resetCode}
            onChange={(e) => setResetCode(e.target.value.replace(/\D/g, ""))}
            placeholder="Código (6 dígitos)"
            className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-center text-2xl font-mono tracking-[0.5em] text-white placeholder-white/20 outline-none focus:border-pink-400/50"
          />
          <PasswordInput
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Nueva contraseña (6+ caracteres)"
            testId="jgfans-new-password"
          />
          <button
            onClick={submitReset}
            disabled={busy}
            className="w-full py-3 bg-gradient-to-r from-pink-500 to-purple-500 rounded-xl font-bold text-sm disabled:opacity-50"
          >
            {busy ? "Actualizando…" : "Cambiar contraseña"}
          </button>
          <button
            onClick={() => setMode("login")}
            className="w-full text-xs text-white/50 hover:text-white"
          >
            Cancelar
          </button>
        </>
      )}
    </div>
  );
};

// ============== SUCCESS PAGE: /jgfans/:username/exito ==============
export const JGFansSuccess = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // Bold sends back ?reference= or ?bold-tx-status=
    const ref = params.get("reference") || params.get("bold-order-id");
    const status = (params.get("bold-tx-status") || "").toLowerCase();
    if (status && ["rejected", "failed", "cancelled"].includes(status)) {
      setStatus("error");
      return;
    }
    if (!ref) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await fetch(`${API}/api/jgfans/${username}/bold/status/${ref}`);
        const d = await r.json();
        if (d.status === "completed" || d.paymentStatus === "approved") {
          setStatus("paid");
          setTimeout(() => navigate(`/jgfans/${username}`), 2500);
          return;
        }
        if (attempts >= 20) {
          setStatus("error");
          return;
        }
        setTimeout(poll, 3000);
      } catch {
        if (attempts >= 20) setStatus("error");
        else setTimeout(poll, 3000);
      }
    };
    poll();
  }, [username, location.search, navigate]);

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
      <div className="max-w-sm text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-pink-400 animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Confirmando tu pago…</h1>
            <p className="text-white/50 text-sm">Un momento, esto suele tomar pocos segundos</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center mx-auto mb-4 shadow-2xl shadow-pink-500/40">
              <Heart className="w-8 h-8" />
            </div>
            <h1 className="text-3xl font-black mb-2">¡Bienvenido(a)! 💋</h1>
            <p className="text-white/70 mb-4">Tu suscripción está activa.</p>
            <p className="text-white/40 text-xs">
              Redirigiendo… Te enviaremos un código a tu email cada vez que quieras entrar.
            </p>
          </>
        )}
        {status === "error" && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center mx-auto mb-4">
              <X className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold mb-2">No pudimos confirmar el pago</h1>
            <p className="text-white/50 text-sm mb-4">
              Si el cobro pasó, espera unos minutos y vuelve a intentar entrar.
            </p>
            <button
              onClick={() => navigate(`/jgfans/${username}`)}
              className="px-6 py-2.5 bg-white text-black rounded-xl font-bold text-sm"
            >
              Volver
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ============== HELPERS for Instagram-style feed ==============
const seedHash = (s) => {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = (h * 31 + s.charCodeAt(i)) & 0xffffffff;
  return Math.abs(h);
};
const seedLikes = (id) => 240 + (seedHash(id) % 8400);
const seedComments = (id) => 6 + (seedHash(id + "c") % 180);
const formatK = (n) => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(".0", "") + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(".0", "") + "K";
  return String(n);
};
const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "ahora";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `hace ${Math.floor(diff / 86400)} d`;
  return `hace ${Math.floor(diff / 604800)} sem`;
};
const daysLeft = (iso) => {
  if (!iso) return 0;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));
};

// Icon catalog used in highlights (shared with admin editor)
export const HIGHLIGHT_ICONS = {
  sparkles: Sparkles, heart: Heart, image: ImageIcon, play: Play, bookmark: Bookmark,
  star: Star, fire: Flame, crown: Crown, diamond: Gem, lock: Lock, flower: Flower2,
  camera: Camera, music: Music, map: MapPin, coffee: Coffee, plane: Plane, zap: Zap,
  moon: Moon, sun: Sun, cloud: Cloud, gift: Gift, message: MessageCircle, smile: Smile,
};
const HighlightIcon = ({ name, className = "w-5 h-5" }) => {
  const Cmp = HIGHLIGHT_ICONS[name] || Sparkles;
  return <Cmp className={className} />;
};

// ============== FEED PAGE: /jgfans/:username/feed (IG + OF hybrid) ==============
export const JGFansFeed = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [token, setToken] = useTokenForCreator(username);
  const [items, setItems] = useState([]);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("feed"); // feed | grid | reels | saved
  const [openPost, setOpenPost] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatPrefill, setChatPrefill] = useState("");
  const [likes, setLikes] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`jgfans_likes_${username}`) || "{}"); }
    catch { return {}; }
  });
  const [saved, setSaved] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`jgfans_saved_${username}`) || "{}"); }
    catch { return {}; }
  });
  const [reactions, setReactions] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`jgfans_reactions_${username}`) || "{}"); }
    catch { return {}; }
  });

  useEffect(() => {
    localStorage.setItem(`jgfans_likes_${username}`, JSON.stringify(likes));
  }, [likes, username]);
  useEffect(() => {
    localStorage.setItem(`jgfans_saved_${username}`, JSON.stringify(saved));
  }, [saved, username]);
  useEffect(() => {
    localStorage.setItem(`jgfans_reactions_${username}`, JSON.stringify(reactions));
  }, [reactions, username]);

  useEffect(() => {
    if (!token) {
      navigate(`/jgfans/${username}`);
      return;
    }
    Promise.all([
      fetch(`${API}/api/jgfans/${username}/info`).then((r) => (r.ok ? r.json() : null)),
      fetch(`${API}/api/jgfans/${username}/feed`, {
        headers: { "X-JGFans-Token": token },
      }).then((r) => {
        if (r.status === 401 || r.status === 402) {
          setToken("");
          navigate(`/jgfans/${username}`);
          return null;
        }
        return r.ok ? r.json() : null;
      }),
    ])
      .then(([i, f]) => {
        setInfo(i);
        if (f) setItems(f.items || []);
      })
      .finally(() => setLoading(false));
  }, [username, token, navigate, setToken]);

  // If the user just returned from Bold (e.g. PPV unlock), poll status and reload chat
  useEffect(() => {
    if (!token) return;
    const url = new URL(window.location.href);
    const reference = url.searchParams.get("reference");
    if (!reference) return;
    let attempts = 0;
    const maxAttempts = 12;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await fetch(`${API}/api/jgfans/${username}/tip/bold/status/${reference}`);
        if (r.ok) {
          const d = await r.json();
          if (d.status === "completed") {
            toast.success(d.kind === "jgfans_ppv_unlock" ? "✓ Contenido desbloqueado" : "✓ Pago confirmado");
            // Clean URL and force reload of chat by opening it
            url.searchParams.delete("reference");
            window.history.replaceState({}, "", url.pathname);
            setChatOpen(true);
            return;
          }
        }
      } catch (e) { /* keep trying */ }
      if (attempts < maxAttempts) setTimeout(poll, 2500);
    };
    poll();
  }, [token, username]);

  const logout = useCallback(() => {
    setToken("");
    navigate(`/jgfans/${username}`);
  }, [setToken, navigate, username]);

  const visible = useMemo(() => {
    if (tab === "grid") return items.filter((i) => i.mediaType !== "video");
    if (tab === "reels") return items.filter((i) => i.mediaType === "video");
    if (tab === "saved") return items.filter((i) => saved[i.id]);
    return items; // feed: everything
  }, [items, tab, saved]);

  const toggleLike = useCallback((id) => setLikes((p) => ({ ...p, [id]: !p[id] })), []);
  const toggleSave = useCallback((id) => setSaved((p) => ({ ...p, [id]: !p[id] })), []);
  const setReaction = useCallback((id, emoji) => {
    setReactions((p) => ({ ...p, [id]: p[id] === emoji ? null : emoji }));
  }, []);

  const openTip = useCallback(() => {
    setTipOpen(true);
  }, []);

  const [tipOpen, setTipOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-rose-300/60 animate-spin" />
      </div>
    );
  }

  const HIGHLIGHTS = (info?.highlights && info.highlights.length > 0)
    ? info.highlights
    : [
        { id: "vip", title: "VIP", icon: "sparkles" },
        { id: "new", title: "Nuevo", icon: "heart" },
        { id: "fotos", title: "Fotos", icon: "image" },
        { id: "reels", title: "Reels", icon: "play" },
        { id: "fav", title: "Favoritos", icon: "bookmark" },
      ];

  const daysRemaining = daysLeft(info?.expiresAt) || info?.accessDays || 30;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="jgfans-feed-page">
      {/* ===== Top bar ===== */}
      <header className="sticky top-0 z-30 bg-black/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 h-12 flex items-center justify-between">
          <button
            onClick={() => navigate(`/${username}`)}
            className="text-white/80 hover:text-white p-1 -ml-1"
            aria-label="Volver"
            data-testid="jgfans-back-btn"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-1.5">
            <Crown className="w-3.5 h-3.5 text-amber-300" />
            <h1 className="font-bold tracking-tight text-[17px]">{info?.username}</h1>
          </div>
          <button
            onClick={() => setChatOpen(true)}
            className="text-white/90 hover:text-white p-1 -mr-1 relative"
            aria-label="Mensajes"
            data-testid="jgfans-open-chat"
          >
            <Send className="w-6 h-6" style={{ transform: "rotate(20deg)" }} />
          </button>
        </div>
      </header>

      {/* ===== VIP status banner (rose-gold) ===== */}
      <div className="bg-gradient-to-r from-amber-500/15 via-rose-500/15 to-amber-500/15 border-b border-amber-400/20">
        <div className="max-w-3xl mx-auto px-4 py-2 flex items-center justify-between text-[12px]">
          <span className="inline-flex items-center gap-1.5 font-semibold text-amber-100">
            <Crown className="w-3.5 h-3.5 text-amber-300" />
            Acceso VIP activo
          </span>
          <span className="text-amber-200/80">
            <strong className="text-amber-200">{daysRemaining}</strong> días restantes
          </span>
        </div>
      </div>

      {/* ===== Profile section ===== */}
      <section className="max-w-3xl mx-auto px-4 pt-5">
        <div className="flex items-center gap-4 sm:gap-8">
          <div className="shrink-0">
            <div className="p-[2.5px] rounded-full bg-gradient-to-tr from-amber-300 via-rose-500 to-fuchsia-600">
              <div className="bg-black p-[3px] rounded-full">
                <img
                  src={info?.avatar || "/logo.png"}
                  alt={info?.name}
                  decoding="async"
                  className="w-[78px] h-[78px] sm:w-[96px] sm:h-[96px] rounded-full object-cover"
                />
              </div>
            </div>
          </div>
          <div className="flex-1 grid grid-cols-3 gap-1 text-center">
            <Stat n={items.length} label="posts" />
            <Stat n={formatK(seedLikes(username) * 12)} label="fans" />
            <Stat n={daysRemaining} label="días vip" />
          </div>
        </div>

        {/* Bio */}
        <div className="mt-4">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-bold">{info?.name}</p>
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gradient-to-br from-amber-300 to-rose-500" title="Verificada">
              <UserCheck className="w-2.5 h-2.5 text-white" />
            </span>
          </div>
          <p className="text-[11px] uppercase tracking-wider mt-0.5 font-bold bg-gradient-to-r from-amber-200 via-rose-300 to-fuchsia-300 bg-clip-text text-transparent">
            👑 Creator JGFans Verificada
          </p>
          {(info?.bio || info?.description) && (
            <p className="text-[13.5px] text-white/85 mt-1.5 leading-relaxed whitespace-pre-line">
              {info?.bio || info?.description}
            </p>
          )}
          <p className="text-[11px] text-white/40 mt-1 font-mono">@{info?.username}</p>
        </div>

        {/* Action buttons */}
        <div className="mt-4 grid grid-cols-12 gap-2">
          <button
            disabled
            className="col-span-4 py-2 bg-gradient-to-br from-amber-400/15 to-rose-500/15 rounded-lg text-sm font-bold border border-amber-400/30 text-amber-100 inline-flex items-center justify-center gap-1.5"
            data-testid="jgfans-subscribed-badge"
          >
            <Crown className="w-3.5 h-3.5 text-amber-300" />
            Suscrito
          </button>
          <button
            onClick={() => setChatOpen(true)}
            className="col-span-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm font-semibold border border-white/10 transition"
            data-testid="jgfans-message-btn"
          >
            Mensaje
          </button>
          <button
            onClick={openTip}
            className="col-span-3 py-2 rounded-lg text-sm font-bold border border-amber-400/40 bg-gradient-to-br from-amber-500/20 to-rose-500/20 hover:from-amber-500/30 hover:to-rose-500/30 transition inline-flex items-center justify-center gap-1 text-amber-100"
            data-testid="jgfans-tip-btn"
          >
            <Gem className="w-3.5 h-3.5" />
            Apoyar
          </button>
          <button
            onClick={logout}
            className="col-span-1 py-2 bg-white/5 hover:bg-white/10 rounded-lg border border-white/10 inline-flex items-center justify-center"
            title="Salir"
            data-testid="jgfans-logout-btn"
          >
            <Share2 className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* ===== Highlights ===== */}
      <section className="max-w-3xl mx-auto px-4 pt-5">
        <div className="flex gap-4 overflow-x-auto no-scrollbar pb-1">
          {HIGHLIGHTS.map((h) => (
            <div key={h.id} className="flex flex-col items-center gap-1.5 min-w-[64px] shrink-0">
              <div className="w-16 h-16 rounded-full p-[2px] bg-gradient-to-tr from-amber-300/80 via-rose-500/70 to-fuchsia-500/70">
                {h.coverUrl ? (
                  <div className="w-full h-full rounded-full bg-black p-[2px]">
                    <img
                      src={h.coverUrl}
                      alt={h.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full rounded-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-full h-full rounded-full bg-black flex items-center justify-center text-amber-200 border border-white/10">
                    <HighlightIcon name={h.icon} />
                  </div>
                )}
              </div>
              <span className="text-[11px] text-white/70 truncate max-w-[68px]">{h.title}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ===== Tabs ===== */}
      <nav className="border-t border-white/10 mt-5 sticky top-12 z-20 bg-black/95 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto flex">
          <TabButton active={tab === "feed"} onClick={() => setTab("feed")} icon={<Sparkles className="w-5 h-5" />} label="Feed" testid="tab-feed" />
          <TabButton active={tab === "grid"} onClick={() => setTab("grid")} icon={<Grid3x3 className="w-5 h-5" />} label="Posts" testid="tab-grid" />
          <TabButton active={tab === "reels"} onClick={() => setTab("reels")} icon={<Film className="w-5 h-5" />} label="Reels" testid="tab-reels" />
          <TabButton active={tab === "saved"} onClick={() => setTab("saved")} icon={<Bookmark className="w-5 h-5" />} label="Guardados" testid="tab-saved" />
        </div>
      </nav>

      {/* ===== Content ===== */}
      <section className="max-w-3xl mx-auto pb-12">
        {visible.length === 0 ? (
          <EmptyFeed name={info?.name} tab={tab} />
        ) : tab === "grid" || tab === "reels" ? (
          <div className="grid grid-cols-3 gap-[2px] sm:gap-1">
            {visible.map((it, idx) => (
              <button
                key={it.id}
                onClick={() => setOpenPost({ item: it, index: idx, list: visible })}
                className="aspect-square relative bg-white/5 overflow-hidden group"
                data-testid={`grid-item-${it.id}`}
              >
                {it.mediaType === "video" ? (
                  <>
                    <video
                      src={it.mediaUrl}
                      className="w-full h-full object-cover"
                      muted
                      preload="none"
                      playsInline
                    />
                    <Film className="absolute top-2 right-2 w-4 h-4 text-white drop-shadow-lg" />
                  </>
                ) : (
                  <img
                    src={it.mediaUrl}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                )}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-5 text-white text-[13px] font-bold">
                  <span className="inline-flex items-center gap-1.5">
                    <Heart className="w-4 h-4 fill-white" /> {formatK(seedLikes(it.id))}
                  </span>
                </div>
              </button>
            ))}
          </div>
        ) : (
          // OF-style vertical feed
          <div className="px-3 sm:px-0 space-y-4 pt-3">
            {visible.map((it) => (
              <FeedPostCard
                key={it.id}
                post={it}
                info={info}
                liked={!!likes[it.id]}
                savedFlag={!!saved[it.id]}
                reaction={reactions[it.id] || null}
                onLike={() => toggleLike(it.id)}
                onSave={() => toggleSave(it.id)}
                onReact={(emoji) => setReaction(it.id, emoji)}
                onComment={() => setChatOpen(true)}
                onTip={openTip}
              />
            ))}
          </div>
        )}
      </section>

      {/* ===== Modals ===== */}
      {openPost && (
        <PostDetailModal
          post={openPost.item}
          index={openPost.index}
          list={openPost.list}
          info={info}
          liked={!!likes[openPost.item.id]}
          saved={!!saved[openPost.item.id]}
          onLike={() => toggleLike(openPost.item.id)}
          onSave={() => toggleSave(openPost.item.id)}
          onClose={() => setOpenPost(null)}
          onPrev={() => {
            const i = openPost.index - 1;
            if (i >= 0) setOpenPost({ item: openPost.list[i], index: i, list: openPost.list });
          }}
          onNext={() => {
            const i = openPost.index + 1;
            if (i < openPost.list.length) setOpenPost({ item: openPost.list[i], index: i, list: openPost.list });
          }}
          onOpenChat={() => { setOpenPost(null); setChatOpen(true); }}
        />
      )}

      {chatOpen && (
        <ChatPanel
          username={username}
          token={token}
          onClose={() => { setChatOpen(false); setChatPrefill(""); }}
          info={info}
          prefill={chatPrefill}
        />
      )}

      {tipOpen && (
        <TipModal
          username={username}
          token={token}
          creatorName={info?.name}
          onClose={() => setTipOpen(false)}
        />
      )}
    </div>
  );
};

// ============== Sub-components ==============
const Stat = ({ n, label }) => (
  <div>
    <p className="text-[17px] font-extrabold leading-none">{typeof n === "number" ? formatK(n) : n}</p>
    <p className="text-[12px] text-white/65 mt-0.5">{label}</p>
  </div>
);

const TabButton = ({ active, onClick, icon, label, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[11px] uppercase tracking-[0.18em] font-bold border-t-[1.5px] -mt-px transition ${
      active ? "border-white text-white" : "border-transparent text-white/45 hover:text-white/80"
    }`}
  >
    {icon}
    <span className="hidden sm:inline">{label}</span>
  </button>
);

const EmptyFeed = ({ name, tab }) => {
  const messages = {
    feed: { title: "Aún no hay publicaciones", sub: `${name || "Esta modelo"} todavía no ha subido contenido. Vuelve pronto 💋` },
    grid: { title: "Aún no hay fotos", sub: `${name || "Esta modelo"} todavía no ha subido fotos.` },
    reels: { title: "Aún no hay reels", sub: "Próximamente videos exclusivos" },
    saved: { title: "Sin guardados", sub: "Toca el bookmark en cualquier publicación para que aparezca aquí" },
  };
  const m = messages[tab] || messages.feed;
  return (
    <div className="text-center py-20 px-6">
      <div className="w-20 h-20 rounded-full border-2 border-amber-300/30 flex items-center justify-center mx-auto mb-4">
        {tab === "reels" ? <Film className="w-9 h-9 text-amber-200/70" /> :
          tab === "saved" ? <Bookmark className="w-9 h-9 text-amber-200/70" /> :
          tab === "grid" ? <Grid3x3 className="w-9 h-9 text-amber-200/70" /> :
          <Sparkles className="w-9 h-9 text-amber-200/70" />}
      </div>
      <h2 className="text-2xl font-extrabold tracking-tight mb-1">{m.title}</h2>
      <p className="text-white/55 text-sm leading-relaxed max-w-xs mx-auto">{m.sub}</p>
    </div>
  );
};

// ============== FEED POST CARD (OnlyFans-style vertical post) ==============
const REACTIONS = ["🔥", "💋", "😍", "❤️", "⭐"];

const FeedPostCard = memo(function FeedPostCard({
  post, info, liked, savedFlag, reaction, onLike, onSave, onReact, onComment, onTip,
}) {
  const [doubleTap, setDoubleTap] = useState(false);
  const baseLikes = seedLikes(post.id);
  const totalLikes = baseLikes + (liked ? 1 : 0);
  const handleDouble = () => {
    if (!liked) onLike();
    setDoubleTap(true);
    setTimeout(() => setDoubleTap(false), 700);
  };
  return (
    <article
      className="bg-zinc-950/60 border border-white/10 rounded-2xl overflow-hidden shadow-xl shadow-black/30"
      data-testid={`feed-card-${post.id}`}
    >
      {/* Header */}
      <div className="flex items-center gap-2.5 px-3.5 py-2.5">
        <div className="p-[1.5px] rounded-full bg-gradient-to-tr from-amber-300 via-rose-500 to-fuchsia-600">
          <img
            src={info?.avatar || "/logo.png"}
            alt=""
            decoding="async"
            className="w-8 h-8 rounded-full object-cover bg-black p-[1px]"
          />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold leading-tight flex items-center gap-1">
            {info?.username}
            <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-gradient-to-br from-amber-300 to-rose-500">
              <UserCheck className="w-2 h-2 text-white" />
            </span>
          </p>
          <p className="text-[10.5px] text-white/45">
            <span className="text-amber-300/90 font-bold">VIP</span> · {timeAgo(post.createdAt)}
          </p>
        </div>
        <button className="text-white/60 hover:text-white p-1" aria-label="Más">
          <MoreHorizontal className="w-5 h-5" />
        </button>
      </div>

      {/* Media */}
      <div className="relative bg-black flex items-center justify-center" onDoubleClick={handleDouble}>
        {post.mediaType === "video" ? (
          <video
            src={post.mediaUrl}
            className="w-full max-h-[78vh] object-contain bg-black"
            controls
            preload="none"
            playsInline
          />
        ) : (
          <img
            src={post.mediaUrl}
            alt=""
            loading="lazy"
            decoding="async"
            className="w-full max-h-[78vh] object-contain select-none"
          />
        )}
        {doubleTap && (
          <Heart className="absolute w-28 h-28 text-white fill-white drop-shadow-2xl pointer-events-none animate-heartPop" />
        )}
        {/* Subtle gold corner badge */}
        <div className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 bg-black/60 backdrop-blur px-2 py-0.5 rounded-full border border-amber-300/30">
          <Crown className="w-3 h-3 text-amber-300" />
          <span className="text-[10px] font-bold tracking-wide text-amber-100">EXCLUSIVO</span>
        </div>
      </div>

      {/* Action bar */}
      <div className="px-3.5 pt-2.5 pb-1 flex items-center justify-between">
        <div className="flex items-center gap-3.5">
          <button onClick={onLike} className="active:scale-90 transition" aria-label="Me gusta">
            <Heart className={`w-7 h-7 transition ${liked ? "fill-rose-500 text-rose-500 animate-heartPop" : "text-white"}`} />
          </button>
          <button onClick={onComment} className="active:scale-90 transition" aria-label="Comentar">
            <MessageCircle className="w-7 h-7 text-white" />
          </button>
          <button onClick={onComment} className="active:scale-90 transition" aria-label="Compartir">
            <Send className="w-7 h-7 text-white" style={{ transform: "rotate(20deg)" }} />
          </button>
          <button
            onClick={onTip}
            className="active:scale-90 transition inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-amber-500/20 to-rose-500/20 border border-amber-300/40 text-amber-100 text-[12px] font-bold hover:from-amber-500/30 hover:to-rose-500/30"
            data-testid={`tip-${post.id}`}
          >
            <Gem className="w-3.5 h-3.5" />
            Apoyar
          </button>
        </div>
        <button onClick={onSave} className="active:scale-90 transition" aria-label="Guardar">
          <Bookmark className={`w-6 h-6 ${savedFlag ? "fill-amber-300 text-amber-300" : "text-white"}`} />
        </button>
      </div>

      {/* Reactions row */}
      <div className="px-3.5 pt-1">
        <div className="flex items-center gap-1.5">
          {REACTIONS.map((emoji) => (
            <button
              key={emoji}
              onClick={() => onReact(emoji)}
              className={`text-lg w-8 h-8 rounded-full transition active:scale-90 ${
                reaction === emoji
                  ? "bg-gradient-to-br from-amber-500/30 to-rose-500/30 border border-amber-300/50 scale-110"
                  : "bg-white/5 hover:bg-white/10 border border-white/10"
              }`}
              data-testid={`react-${post.id}-${emoji}`}
            >
              {emoji}
            </button>
          ))}
          <span className="text-[11px] text-white/40 ml-1">
            {reaction ? `Reaccionaste con ${reaction}` : "Reacciona"}
          </span>
        </div>
      </div>

      {/* Stats + caption */}
      <div className="px-3.5 pt-2 pb-3.5">
        <p className="text-sm font-bold">{formatK(totalLikes)} me gusta</p>
        {post.caption && (
          <p className="text-sm text-white/90 mt-1 leading-relaxed whitespace-pre-wrap">
            <span className="font-bold mr-1.5">{info?.username}</span>
            {post.caption}
          </p>
        )}
        <p className="text-[10.5px] text-white/35 uppercase tracking-wider mt-2">
          {timeAgo(post.createdAt)}
        </p>
      </div>
    </article>
  );
});

// (BottomNav removed — tabs are sticky now and serve as primary navigation)

// ============== POST DETAIL MODAL (Instagram-style) ==============
const PostDetailModal = ({ post, index, list, info, liked, saved, onLike, onSave, onClose, onPrev, onNext, onOpenChat }) => {
  const [doubleTap, setDoubleTap] = useState(false);
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, onPrev, onNext]);

  const handleDoubleClick = () => {
    if (!liked) onLike();
    setDoubleTap(true);
    setTimeout(() => setDoubleTap(false), 700);
  };

  const baseLikes = seedLikes(post.id);
  const totalLikes = baseLikes + (liked ? 1 : 0);

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="post-modal">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 h-14 border-b border-white/10 shrink-0">
        <button onClick={onClose} className="text-white/90 hover:text-white" data-testid="post-modal-close">
          <ArrowLeft className="w-6 h-6" />
        </button>
        <img src={info?.avatar || "/logo.png"} alt="" className="w-8 h-8 rounded-full object-cover ring-1 ring-pink-500/40" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold truncate flex items-center gap-1">
            {info?.username}
            <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-gradient-to-br from-pink-500 to-purple-500">
              <UserCheck className="w-2 h-2 text-white" />
            </span>
          </p>
          <p className="text-[11px] text-white/45">Publicación VIP</p>
        </div>
        <button className="text-white/80 hover:text-white">
          <MoreHorizontal className="w-5 h-5" />
        </button>
      </div>

      {/* Media */}
      <div className="flex-1 relative bg-black flex items-center justify-center overflow-hidden" onDoubleClick={handleDoubleClick}>
        {post.mediaType === "video" ? (
          <video src={post.mediaUrl} className="max-h-full max-w-full object-contain" controls autoPlay playsInline />
        ) : (
          <img src={post.mediaUrl} alt="" className="max-h-full max-w-full object-contain select-none" />
        )}
        {/* Double-tap heart */}
        {doubleTap && (
          <Heart className="absolute w-32 h-32 text-white fill-white drop-shadow-2xl pointer-events-none animate-heartPop" />
        )}
        {/* Prev/Next desktop */}
        {index > 0 && (
          <button onClick={onPrev} className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur items-center justify-center">
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        {index < list.length - 1 && (
          <button onClick={onNext} className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur items-center justify-center">
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Action bar + caption */}
      <div className="shrink-0 border-t border-white/10 bg-black px-4 pt-2 pb-3 max-h-[40vh] overflow-y-auto">
        <div className="flex items-center justify-between py-1">
          <div className="flex items-center gap-4">
            <button
              onClick={onLike}
              className="active:scale-90 transition"
              data-testid="post-like-btn"
              aria-label="Me gusta"
            >
              <Heart className={`w-7 h-7 transition ${liked ? "fill-red-500 text-red-500 animate-heartPop" : "text-white"}`} />
            </button>
            <button onClick={onOpenChat} className="active:scale-90 transition" aria-label="Comentar">
              <MessageCircle className="w-7 h-7 text-white" />
            </button>
            <button onClick={onOpenChat} className="active:scale-90 transition" aria-label="Compartir">
              <Send className="w-7 h-7 text-white" style={{ transform: "rotate(20deg)" }} />
            </button>
          </div>
          <button onClick={onSave} className="active:scale-90 transition" data-testid="post-save-btn" aria-label="Guardar">
            <Bookmark className={`w-6 h-6 ${saved ? "fill-white text-white" : "text-white"}`} />
          </button>
        </div>

        <p className="text-sm font-bold mt-1">{formatK(totalLikes)} me gusta</p>

        {post.caption && (
          <p className="text-sm text-white/90 mt-1 leading-relaxed whitespace-pre-wrap">
            <span className="font-bold mr-1.5">{info?.username}</span>
            {post.caption}
          </p>
        )}

        <button
          onClick={onOpenChat}
          className="text-[13px] text-white/45 mt-1.5 block hover:text-white/70"
        >
          Ver los {seedComments(post.id)} comentarios
        </button>

        <p className="text-[11px] text-white/35 uppercase tracking-wider mt-1.5">
          {timeAgo(post.createdAt)}
        </p>
      </div>
    </div>
  );
};

// ============== CHAT PANEL (Instagram DM style) ==============
const ChatPanel = ({ username, token, onClose, info, prefill = "" }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState(prefill);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    const r = await fetch(`${API}/api/jgfans/${username}/chat`, {
      headers: { "X-JGFans-Token": token },
    });
    if (r.ok) {
      const d = await r.json();
      setMessages(d.messages || []);
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async () => {
    if (!input.trim()) return;
    setSending(true);
    const text = input;
    setInput("");
    try {
      const r = await fetch(`${API}/api/jgfans/${username}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-JGFans-Token": token },
        body: JSON.stringify({ text }),
      });
      if (r.ok) {
        const m = await r.json();
        setMessages((prev) => [...prev, m]);
      } else {
        toast.error("No se pudo enviar");
      }
    } finally {
      setSending(false);
    }
  };

  // Group messages into "blocks" per sender for Instagram-like bubble grouping
  const grouped = [];
  messages.forEach((m, i) => {
    const prev = messages[i - 1];
    const sameSender = prev && prev.sender === m.sender;
    if (sameSender) grouped[grouped.length - 1].messages.push(m);
    else grouped.push({ sender: m.sender, messages: [m] });
  });

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="chat-panel">
      {/* Top bar (Instagram DM style) */}
      <div className="flex items-center gap-3 px-3 h-14 border-b border-white/10 shrink-0">
        <button onClick={onClose} className="text-white/90 hover:text-white p-1" aria-label="Cerrar chat">
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div className="relative shrink-0">
          <div className="p-[1.5px] rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600">
            <div className="bg-black p-[1.5px] rounded-full">
              <img src={info?.avatar || "/logo.png"} alt="" className="w-9 h-9 rounded-full object-cover" />
            </div>
          </div>
          <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-black" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm truncate flex items-center gap-1">
            {info?.name}
            <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-gradient-to-br from-pink-500 to-purple-500">
              <UserCheck className="w-2 h-2 text-white" />
            </span>
          </p>
          <p className="text-[11px] text-emerald-400">En línea</p>
        </div>
        <button className="text-white/85 hover:text-white p-1.5" aria-label="Llamar">
          <Phone className="w-5 h-5" />
        </button>
        <button className="text-white/85 hover:text-white p-1.5" aria-label="Videollamada">
          <VideoIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-4">
        {/* Profile block at top of empty conversation */}
        <div className="flex flex-col items-center text-center pt-2 pb-4">
          <div className="p-[2px] rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 mb-2">
            <div className="bg-black p-[2px] rounded-full">
              <img src={info?.avatar || "/logo.png"} alt="" className="w-20 h-20 rounded-full object-cover" />
            </div>
          </div>
          <p className="font-bold text-base flex items-center gap-1">
            {info?.name}
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gradient-to-br from-pink-500 to-purple-500">
              <UserCheck className="w-2.5 h-2.5 text-white" />
            </span>
          </p>
          <p className="text-[12px] text-white/45 mt-0.5">@{info?.username} · JGFans</p>
          <p className="text-[12px] text-white/55 mt-1">Suscripción activa</p>
        </div>

        {messages.length === 0 && (
          <div className="text-center text-white/55 text-sm py-6 px-8 leading-relaxed">
            <p className="text-3xl mb-2">👋</p>
            <p>Saluda a {info?.name?.split(" ")[0] || info?.username}</p>
            <p className="text-xs text-white/35 mt-1">Te responde lo antes posible</p>
          </div>
        )}

        {grouped.map((block, gi) => (
          <div key={gi} className={`flex ${block.sender === "subscriber" ? "justify-end" : "justify-start"} gap-2`}>
            {block.sender !== "subscriber" && (
              <img
                src={info?.avatar || "/logo.png"}
                alt=""
                className="w-7 h-7 rounded-full object-cover self-end mb-1 shrink-0"
              />
            )}
            <div className="flex flex-col gap-0.5 max-w-[78%]">
              {block.messages.map((m, mi) => {
                const isMe = m.sender === "subscriber";
                const isFirst = mi === 0;
                const isLast = mi === block.messages.length - 1;
                const hasMedia = m.media && (m.media.mediaUrl || m.media.priceCOP > 0);
                return (
                  <div key={m.id} className="flex flex-col gap-1">
                    {hasMedia && (
                      <ChatMediaBubble
                        msg={m}
                        username={username}
                        token={token}
                        isMe={isMe}
                      />
                    )}
                    {m.text && (
                      <div
                        className={`px-3.5 py-2 text-[14px] leading-snug whitespace-pre-wrap break-words ${
                          isMe
                            ? "bg-gradient-to-br from-pink-500 to-purple-600 text-white"
                            : "bg-white/[0.08] border border-white/5 text-white"
                        }`}
                        style={{
                          borderTopLeftRadius: isMe ? 22 : isFirst ? 22 : 6,
                          borderTopRightRadius: isMe ? (isFirst ? 22 : 6) : 22,
                          borderBottomLeftRadius: isMe ? 22 : isLast ? 22 : 6,
                          borderBottomRightRadius: isMe ? (isLast ? 6 : 6) : 22,
                        }}
                      >
                        {m.text}
                      </div>
                    )}
                  </div>
                );
              })}
              <p className={`text-[10px] text-white/30 mt-0.5 ${block.sender === "subscriber" ? "text-right" : ""}`}>
                {timeAgo(block.messages[block.messages.length - 1].createdAt)}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Input */}
      <div className="shrink-0 px-3 pb-3 pt-2 bg-black">
        <div className="flex items-center gap-2 bg-white/[0.06] border border-white/10 rounded-full pl-3 pr-1.5 py-1">
          <Smile className="w-5 h-5 text-white/55 shrink-0" />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Mensaje…"
            data-testid="chat-input"
            className="flex-1 bg-transparent py-2 text-[14px] text-white placeholder-white/35 outline-none"
          />
          {input.trim() ? (
            <button
              onClick={send}
              disabled={sending}
              data-testid="chat-send"
              className="text-pink-400 hover:text-pink-300 font-bold text-sm pr-2 disabled:opacity-50"
            >
              {sending ? "…" : "Enviar"}
            </button>
          ) : (
            <button
              onClick={() => setInput((p) => p + " ❤️")}
              className="w-8 h-8 rounded-full flex items-center justify-center text-pink-400 hover:bg-white/5"
              aria-label="Corazón"
            >
              <Heart className="w-5 h-5 fill-pink-400" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============== TIP MODAL ==============
const TIP_PRESETS = [5000, 10000, 50000];
const TipModal = ({ username, token, creatorName, onClose }) => {
  const [amount, setAmount] = useState(10000);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);

  const finalAmount = custom ? parseInt(custom.replace(/\D/g, ""), 10) || 0 : amount;
  const valid = finalAmount >= 1000;

  const proceed = async () => {
    if (!valid) {
      toast.error("Mínimo $1.000 COP");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/jgfans/${username}/tip/bold/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-JGFans-Token": token },
        body: JSON.stringify({ amount: finalAmount, originUrl: window.location.origin }),
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Error al generar el link de pago");
        return;
      }
      window.location.href = d.url;
    } catch (e) {
      console.error(e);
      toast.error("Error de red");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center p-3 sm:p-6" data-testid="tip-modal">
      <div className="bg-gradient-to-b from-zinc-950 to-black border border-amber-400/30 rounded-3xl w-full max-w-md p-6 shadow-2xl shadow-amber-500/10">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-2xl font-extrabold tracking-tight flex items-center gap-2 text-white">
              <Gem className="w-6 h-6 text-amber-300" />
              Apoyar a {creatorName?.split(" ")[0]}
            </h2>
            <p className="text-white/55 text-sm mt-0.5">Pago instantáneo vía Bold</p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white p-1 rounded-lg hover:bg-white/5">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-3">
          {TIP_PRESETS.map((v) => (
            <button
              key={v}
              onClick={() => { setAmount(v); setCustom(""); }}
              data-testid={`tip-${v}`}
              className={`py-3 rounded-2xl border text-center transition ${
                !custom && amount === v
                  ? "bg-gradient-to-br from-amber-500/30 to-rose-500/30 border-amber-400/70 text-amber-100"
                  : "bg-white/5 border-white/10 text-white/85 hover:bg-white/10"
              }`}
            >
              <p className="text-[10px] uppercase tracking-wider text-white/40">COP</p>
              <p className="text-lg font-extrabold">${v.toLocaleString("es-CO")}</p>
            </button>
          ))}
        </div>

        <div className="mb-4">
          <label className="text-[11px] uppercase tracking-wider text-white/45 font-bold">Personalizado</label>
          <div className="relative mt-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
            <input
              type="text"
              inputMode="numeric"
              value={custom}
              onChange={(e) => setCustom(e.target.value.replace(/\D/g, ""))}
              placeholder="Otra cantidad…"
              data-testid="tip-custom"
              className="w-full bg-black/40 border border-white/10 rounded-xl pl-7 pr-16 py-3 text-white outline-none focus:border-amber-400/50"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 text-xs">COP</span>
          </div>
          {custom && finalAmount > 0 && (
            <p className="text-[11px] text-amber-200/80 mt-1.5">
              Tip: ${finalAmount.toLocaleString("es-CO")} COP
            </p>
          )}
        </div>

        <button
          onClick={proceed}
          disabled={busy || !valid}
          data-testid="tip-proceed"
          className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-amber-400 via-rose-500 to-fuchsia-500 text-white font-bold disabled:opacity-50 hover:brightness-110 inline-flex items-center justify-center gap-2 shadow-lg shadow-rose-500/20"
        >
          <Gem className="w-4 h-4" />
          {busy ? "Generando link…" : `Pagar $${(finalAmount || 0).toLocaleString("es-CO")} COP`}
        </button>
        <p className="text-[10.5px] text-white/35 text-center mt-2.5 leading-relaxed">
          Te redirigimos a Bold para completar el pago. Tu tip se acredita automáticamente.
        </p>
      </div>
    </div>
  );
};


// ============== CHAT MEDIA BUBBLE (PPV-aware) ==============
const ChatMediaBubble = ({ msg, username, token, isMe }) => {
  const [busy, setBusy] = useState(false);
  const media = msg.media || {};
  const unlocked = !!media.unlocked;
  const isPhoto = media.mediaType !== "video";
  const price = parseInt(media.priceCOP || 0, 10);
  const priceStr = "$" + price.toLocaleString("es-CO") + " COP";

  const unlock = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/jgfans/${username}/message/${msg.id}/unlock/bold/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-JGFans-Token": token },
        body: JSON.stringify({ originUrl: window.location.origin }),
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "No se pudo generar el link");
        return;
      }
      if (d.unlocked) {
        toast.success("Ya tenías este contenido desbloqueado");
        return;
      }
      window.location.href = d.url;
    } finally {
      setBusy(false);
    }
  };

  // Unlocked: render real media full
  if (unlocked && media.mediaUrl) {
    return (
      <div
        className={`overflow-hidden rounded-2xl border ${isMe ? "border-pink-400/30" : "border-white/10"} max-w-[280px]`}
        data-testid={`chat-media-${msg.id}`}
      >
        {isPhoto ? (
          <img src={media.mediaUrl} alt="" loading="lazy" decoding="async" className="w-full h-auto block" />
        ) : (
          <video src={media.mediaUrl} controls preload="none" playsInline className="w-full h-auto block bg-black" />
        )}
      </div>
    );
  }

  // Locked
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-amber-300/40 bg-gradient-to-br from-amber-900/30 via-rose-900/20 to-fuchsia-900/30 max-w-[280px] aspect-[4/5] flex items-center justify-center"
      data-testid={`chat-media-locked-${msg.id}`}
    >
      {/* Decorative gradient orbs */}
      <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-amber-400/15 blur-3xl" />
      <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full bg-fuchsia-500/15 blur-3xl" />
      <div className="relative text-center p-4 z-10">
        <div className="w-12 h-12 mx-auto rounded-full bg-black/70 backdrop-blur border border-amber-300/40 flex items-center justify-center mb-2">
          <Lock className="w-5 h-5 text-amber-300" />
        </div>
        <p className="text-xs uppercase tracking-wider font-bold text-amber-200/80">Contenido exclusivo</p>
        <p className="text-2xl font-extrabold text-white mt-1">{priceStr}</p>
        <button
          onClick={unlock}
          disabled={busy}
          data-testid={`chat-media-unlock-${msg.id}`}
          className="mt-3 px-4 py-2 rounded-full bg-gradient-to-r from-amber-400 via-rose-500 to-fuchsia-500 text-white text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50 shadow-lg shadow-rose-500/30"
        >
          <Lock className="w-3.5 h-3.5" />
          {busy ? "Generando…" : "Desbloquear"}
        </button>
        <p className="text-[10px] text-white/55 mt-2 max-w-[200px] mx-auto leading-snug">
          Pago seguro vía Bold. Se desbloquea automáticamente.
        </p>
      </div>
    </div>
  );
};

